'use strict';

const libWorkspace = require('pict-section-workspace');

/**
 * Entity-metadata section — the shared "append the per-customer metadata form UNDERNEATH the Overview's field groups"
 * behavior, factored out so ANY workspace provider can mix it in (not just the stock EntityMetadataWorkspaceProvider
 * below — e.g. CustomerWorkspace-Provider, which has its own bespoke tabs, calls renderMetadataSection() from its own
 * _renderOverview override).
 *
 * The values live on the entity's blob field; the field SHAPE comes from the per-customer EntityMetadata/<Entity>
 * ManifestSchema (SubType = Entity.Name), rendered read-only with an inline Edit/Save by the EntityMetadataForm
 * singleton provider. See [[config-app-entity-metadata]].
 *
 * A workspace opts in by setting, in its workspace-config:
 *   MetadataBlob: { column: '<blob field as the Meadow API names it>', label: '<section heading>', encoding?: 'object' | 'string' }
 * (`column` is the API field name — which can differ from the DB column; `encoding` is only needed when the field can
 * be empty on first save, since a populated record's shape is mirrored automatically.)
 */

const META_HOST_SELECTOR = '#EMW-MetaHost';

/** Append (once) the metadata group to the Overview body + (re)mount the form. Safe to call on every Overview paint. */
function renderMetadataSection(pProvider, pBodySel)
{
	const tmpBlob = pProvider.workspaceConfig && pProvider.workspaceConfig.MetadataBlob;
	const tmpCA = pProvider.pict.ContentAssignment;
	const tmpEl = tmpCA.getElement(pBodySel);
	if (!tmpBlob || !tmpBlob.column || !tmpEl || !tmpEl[0] || !pProvider._record || (typeof document === 'undefined')) { return; }
	// super._renderOverview replaced the body, so re-append our metadata group whenever it is missing.
	if (!tmpEl[0].querySelector(META_HOST_SELECTOR))
	{
		const tmpGroup = document.createElement('div');
		tmpGroup.className = 'pw-group';
		tmpGroup.innerHTML = `<div class="pw-group-head">${pProvider._esc(tmpBlob.label || 'Custom Properties')}</div><div id="EMW-MetaHost"></div>`;
		tmpEl[0].appendChild(tmpGroup);
	}
	_mountMetadata(pProvider, tmpBlob);
}

function _mountMetadata(pProvider, pBlob)
{
	const tmpEMF = pProvider.pict.providers.EntityMetadataForm;
	if (!pBlob || !tmpEMF || !pProvider._record || (typeof tmpEMF.openWorkspaceMetadata !== 'function')) { return; }
	if (!pProvider.pict.ContentAssignment.getElement(META_HOST_SELECTOR).length) { return; }   // host not on screen
	tmpEMF.openWorkspaceMetadata({ entityType: pProvider.entityName, blobColumn: pBlob.column, encoding: pBlob.encoding, record: pProvider._record, idField: pProvider.idField, idCustomer: pProvider._record.IDCustomer, host: META_HOST_SELECTOR });
}

/**
 * EntityMetadataWorkspace-Provider — a REUSABLE workspace ProviderClass whose ONLY customization is to render the
 * configured metadata blob underneath the Overview's field groups. Workspaces whose Overview needs nothing else opt
 * in simply with `ProviderClass: require('../providers/EntityMetadataWorkspace-Provider.js')` + a MetadataBlob.
 */
class EntityMetadataWorkspaceProvider extends libWorkspace.HeadlightWorkspaceBaseProvider
{
	/** Overview = the base's field groups, then the metadata section appended underneath (with the record). */
	_renderOverview(pBodySel)
	{
		super._renderOverview(pBodySel);
		renderMetadataSection(this, pBodySel);
	}
}

EntityMetadataWorkspaceProvider.default_configuration = { ProviderIdentifier: 'EntityMetadataWorkspace' };

module.exports = EntityMetadataWorkspaceProvider;
module.exports.default_configuration = EntityMetadataWorkspaceProvider.default_configuration;
module.exports.renderMetadataSection = renderMetadataSection;
